import { useLayoutEffect } from 'react';

import { SIDE_PANEL_MOTION } from 'components/ui/overlay-motion';
import { useContentAreaRect } from 'layout/MainLayout/useContentAreaRect';

const PANEL_WIDTH = 744;
const PANEL_GAP = 24;

/**
 * The box a side panel draws in, in px: `gap` off the sidebar's edge or the window's right edge and off the
 * window's bottom, and `top` down from the window's top, which is `gap` under the top bar: the content area
 * starts where the top bar ends, whatever banners sit above it. `width` is the one the Timecard center and the
 * Inbox share. It yields on a narrow window to what the content area leaves between two gaps: that area starts
 * at the sidebar's edge, so neither panel reaches the sidebar, whether it is open, the rail, or off-canvas.
 */
export function usePanelBox() {
    const contentRect = useContentAreaRect();
    const room = contentRect ? Math.max(contentRect.width - 2 * PANEL_GAP, 0) : PANEL_WIDTH;
    return { width: Math.min(PANEL_WIDTH, room), gap: PANEL_GAP, top: (contentRect?.top ?? 0) + PANEL_GAP };
}

/**
 * Keeps the box's gap and top on the document, as `--panel-gap` and `--panel-top`, while the new layout is
 * mounted. Every detached panel reads them from there: a drawer portalled to the body has no ancestor in the
 * layout to inherit them from. Renders nothing.
 */
export function PanelBoxProperties() {
    const { gap, top } = usePanelBox();

    useLayoutEffect(() => {
        const { style } = document.documentElement;
        style.setProperty('--panel-gap', `${gap}px`);
        style.setProperty('--panel-top', `${top}px`);
        return () => {
            style.removeProperty('--panel-gap');
            style.removeProperty('--panel-top');
        };
    }, [gap, top]);

    return null;
}

/**
 * A kit side drawer (`swipeDirection="right"`) as that box, for its `DrawerContent`, moving as the other side
 * panels do; the caller adds its width. The kit's inset is its margin on every side, so only the top is placed
 * apart, with no margin above it. The window less two gaps caps the width, and whatever the drawer holds is
 * capped to the drawer. The strip the kit paints past the drawer's outer edge, for a swipe that overshoots,
 * would fill the gap, so it is cleared.
 */
export const DETACHED_DRAWER_CLASS = `${SIDE_PANEL_MOTION} bg-paper shadow-panel outline-hidden mt-0 max-w-[calc(100vw-2*var(--panel-gap))] [--drawer-bleed-background:transparent] [--drawer-inset:var(--panel-gap)] *:*:max-w-full data-[swipe-axis=x]:top-(--panel-top) data-[swipe-direction=right]:rounded-2xl data-[swipe-direction=right]:border-0`;
